import type { Rng, RoundOutcome } from '../../core/contracts'

/**
 * Missing One - зрительная память на объекты.
 *
 * Показывается набор фигур, затем он скрывается. При повторном показе
 * одного места пустует - нужно вспомнить, какая фигура там была.
 *
 * Почему оставшиеся фигуры показываются снова: без этого игрок просто
 * сравнивал бы две картинки, и задача выродилась бы в зрительный поиск.
 * Пустое место заставляет вспоминать конкретный объект, а видимые
 * соседи дают опору для исключения - как в исходной методике.
 *
 * Фигуры, а не цвета и не слова: игра не зависит от языка интерфейса
 * и доступна при нарушениях цветовосприятия.
 */

export type ShapeId =
  | 'circle'
  | 'square'
  | 'triangle'
  | 'diamond'
  | 'star'
  | 'hexagon'
  | 'cross'
  | 'ring'
  | 'arrow'
  | 'moon'

export const SHAPES: readonly ShapeId[] = [
  'circle',
  'square',
  'triangle',
  'diamond',
  'star',
  'hexagon',
  'cross',
  'ring',
  'arrow',
  'moon',
]

export interface MissingTrial {
  /** Набор фигур в порядке показа */
  readonly set: readonly ShapeId[]
  /** Позиция в наборе, которая опустеет */
  readonly missingIndex: number
  /** Варианты ответа, всегда содержат пропавшую фигуру */
  readonly options: readonly ShapeId[]
  /** Сколько показывать набор */
  readonly showMs: number
}

export interface MissingRound {
  readonly trials: readonly MissingTrial[]
}

export interface MissingAnswer {
  /** Ответ на каждый набор; null - не ответил */
  readonly picks: readonly (ShapeId | null)[]
  /** Среднее время верных ответов, мс */
  readonly rtMs: number
}

export interface LevelSpec {
  /** Сколько фигур в наборе - основная ось трудности */
  readonly setSize: number
  /** Сколько наборов в раунде */
  readonly trials: number
  /** Сколько вариантов ответа */
  readonly optionCount: number
  /** Время показа на одну фигуру */
  readonly msPerShape: number
}

const LEVELS: readonly LevelSpec[] = [
  { setSize: 3, trials: 2, optionCount: 4, msPerShape: 600 },
  { setSize: 4, trials: 2, optionCount: 5, msPerShape: 560 },
  { setSize: 4, trials: 3, optionCount: 6, msPerShape: 520 },
  { setSize: 5, trials: 3, optionCount: 6, msPerShape: 480 },
  { setSize: 5, trials: 3, optionCount: 7, msPerShape: 450 },
  { setSize: 6, trials: 3, optionCount: 7, msPerShape: 420 },
  { setSize: 6, trials: 3, optionCount: 8, msPerShape: 400 },
  { setSize: 7, trials: 3, optionCount: 8, msPerShape: 380 },
  { setSize: 7, trials: 3, optionCount: 9, msPerShape: 360 },
  { setSize: 8, trials: 3, optionCount: 9, msPerShape: 345 },
  { setSize: 8, trials: 3, optionCount: 10, msPerShape: 330 },
  { setSize: 9, trials: 3, optionCount: 10, msPerShape: 320 },
]

export const MIN_LEVEL = 1
export const MAX_LEVEL = LEVELS.length
export const INITIAL_LEVEL = 3

/** Рамки времени показа после применения личного темпа. */
export const MIN_SHOW_MS = 1200
export const MAX_SHOW_MS = 5000

/** Эталонное время: вспомнить объект дольше, чем нажать цвет. */
export const REFERENCE_RT_MS = 2200

export function specForLevel(level: number): LevelSpec {
  const index = Math.min(Math.max(level, MIN_LEVEL), MAX_LEVEL) - 1
  const spec = LEVELS[index]
  if (!spec) throw new Error(`Missing One: нет спецификации для уровня ${level}`)
  return spec
}

/**
 * @param pace личный темп игрока: показ растягивается под тех,
 *   кто обычно отвечает медленнее эталона.
 */
export function generateRound(level: number, rng: Rng, pace = 1): MissingRound {
  const spec = specForLevel(level)

  const trials: MissingTrial[] = []
  for (let i = 0; i < spec.trials; i++) {
    const shuffled = rng.shuffle(SHAPES)
    const set = shuffled.slice(0, spec.setSize)
    const missingIndex = rng.int(set.length)
    const missing = set[missingIndex]
    if (!missing) throw new Error('Missing One: пустой набор')

    // варианты: пропавшая фигура плюс отвлекающие.
    // Отвлекающие берём и из набора, и извне - иначе ответ
    // вычислялся бы по одному лишь составу вариантов.
    const distractors = shuffled.filter((s) => s !== missing).slice(0, spec.optionCount - 1)
    const options = rng.shuffle([missing, ...distractors])

    const showMs = Math.round(
      Math.min(Math.max(spec.setSize * spec.msPerShape * pace, MIN_SHOW_MS), MAX_SHOW_MS),
    )

    trials.push({ set, missingIndex, options, showMs })
  }

  return { trials }
}

export function evaluate(round: MissingRound, answer: MissingAnswer): RoundOutcome {
  const total = round.trials.length
  if (total === 0) return { correct: false, accuracy: 0, rtMs: answer.rtMs }

  let hits = 0
  round.trials.forEach((trial, i) => {
    if (answer.picks[i] === trial.set[trial.missingIndex]) hits++
  })

  return {
    correct: hits === total,
    accuracy: hits / total,
    rtMs: answer.rtMs,
  }
}

/** Пропавшая фигура конкретного набора. Для отрисовки и тестов. */
export function missingShapeOf(trial: MissingTrial): ShapeId {
  const shape = trial.set[trial.missingIndex]
  if (!shape) throw new Error('Missing One: индекс вне набора')
  return shape
}
